import api from './api';

const fetchIntegrations = async ({
  commands = null,
  category = null,
  nameSrm = null,
  page = null,
  limit = null,
  audience = null,
  events = null,
  actions = null,
  serviceNetwork = null,
  trigger = null,
  published = null,
  active = null,
  cache = null,
  circuitBreaker = null,
  autoRetry = null,
  metadata = null,
  metadataKey = null,
  metadataValue = null,
  executionConditions = null,
} = {}) => {
  try {
    const requestParams = {
      'filter[name_or_srm_account_cont]': nameSrm,
      'filter[category_in]': category,
      'filter[audience_array_cont]': audience,
      'filter[events_array_cont]': events,
      'filter[action_in]': actions,
      'filter[service_network_array_cont]': serviceNetwork,
      'filter[trigger_type_array_cont]': trigger,
      'filter[commands_flow_array_cont]': commands,
      'filter[published_eq]': published,
      'filter[enabled_eq]': active,
      'filter[cache_enabled_eq]': cache,
      'filter[circuit_breaker_enabled_eq]': circuitBreaker,
      'filter[automatic_retry_enabled_eq]': autoRetry,
      'filter[metadata_present]': metadata,
      'filter[metadata_key_cont]': metadataKey,
      'filter[metadata_value_cont]': metadataValue,
      'filter[execution_conditions_present]': executionConditions,
      'page[number]': page,
      'page[size]': limit,
    };

    // Remove keys with null values
    const filteredRequestParams = Object.fromEntries(
      Object.entries(requestParams).filter(([_key, value]) => value !== null),
    );

    const response = await api.get(`/integrations`, {
      params: filteredRequestParams,
    });

    const { data, meta } = response.data;

    return { data, totalRecords: meta?.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchArchivedIntegrations = async (
  page,
  limit,
  { name = null, archived = true } = {},
) => {
  try {
    const params = {
      'page[number]': page,
      'page[size]': limit,
      archived,
    };
    // Search the integration name directly. The name_or_srm_account_cont scope
    // joins configured_integrations with `discarded_at IS NULL`, which excludes
    // archived integrations (their locations are discarded too), so name_cont is
    // the correct predicate for the archive list.
    if (name) params['filter[name_cont]'] = name;

    const response = await api.get(`/integrations`, { params });

    const { data, meta } = response.data;

    return { data, totalRecords: meta.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchIntegrationByUuid = async (uuid) => {
  try {
    const { data } = await api.get(
      `/integrations/${uuid}?include=integration_step_functions`,
    );

    return { data: data.data, included: data.included };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchIntegrationChangeHistory = async (uuid, page = 1, pageSize = 10) => {
  try {
    const { data } = await api.get(`/integrations/${uuid}/change-history`, {
      params: { 'page[number]': page, 'page[size]': pageSize },
    });

    return { data: data.data, totalRecords: data.meta?.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchIntegrationBasicInfo = async (uuid) => {
  try {
    const { data } = await api.get(`/integrations/${uuid}`);
    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const updateIntegrationStaticAttributes = async (
  uuid,
  params,
  integrationMetadata,
  integrationCacheEnabled,
  integrationCacheTtl,
  circuitBreakerEnabled,
  circuitBreakerThreshold,
  circuitBreakerRetryAfter,
  automaticRetryEnabled,
  retryAfter,
  maxRetries,
  executionConditions,
  timeout,
  autoEnable,
) => {
  try {
    const { data } = await api.put(
      `${process.env.REACT_APP_BASE_URL}/integrations/${uuid}`,
      {
        data: {
          type: 'integrations',
          attributes: {
            global_settings_attributes: Object.values(params),
            metadata: integrationMetadata,
            cache_enabled: integrationCacheEnabled,
            cache_ttl: integrationCacheTtl,
            circuit_breaker_enabled: circuitBreakerEnabled,
            circuit_breaker_failure_threshold: circuitBreakerThreshold,
            circuit_breaker_retry_after: circuitBreakerRetryAfter,
            automatic_retry_enabled: automaticRetryEnabled,
            retry_after: retryAfter,
            max_retries: maxRetries,
            execution_conditions: executionConditions,
            timeout,
            auto_enable: autoEnable,
          },
        },
      },
    );

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const updateIntegration = async (uuid, integrationAttributes) => {
  try {
    const { data } = await api.put(
      `${process.env.REACT_APP_BASE_URL}/integrations/${uuid}`,
      {
        data: {
          type: 'integrations',
          attributes: integrationAttributes,
        },
      },
    );

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const archiveIntegration = async (uuid) => {
  try {
    const response = await api.delete(`/integrations/${uuid}`, { data: {} });

    return response;
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const restoreIntegration = async (uuid) => {
  try {
    const response = await api.put(`/integrations/${uuid}/restore`, {
      data: {
        type: 'integrations',
        attributes: { discarded_at: null },
      },
    });

    return response;
  } catch (e) {
    // The restore endpoint returns 422 with a raw ActiveModel errors hash
    // (e.g. { name: ['has already been taken'] }) rather than a JSON:API
    // `errors` array, so fall back to the raw body when `errors` is absent.
    return { errors: e.response?.data?.errors || e.response?.data };
  }
};

// `meta` carries the Shared Settings cascade summary — which linked integrations
// were also enabled or disabled — so it has to survive back to the caller.
const toggleConfiguredIntegration = async (uuid, enabled) => {
  try {
    const { data } = await api.put(
      `${process.env.REACT_APP_BASE_URL}/configured-integrations/${uuid}`,
      {
        data: {
          type: 'configured_integrations',
          attributes: {
            enabled,
          },
        },
      },
    );

    return { data: data.data, meta: data.meta };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const toggleIntegration = async (uuid, value, integrationField) => {
  try {
    const params = {
      data: {
        type: 'integrations',
        attributes: {},
      },
    };
    params.data.attributes[integrationField] = value;
    const { data } = await api.put(
      `${process.env.REACT_APP_BASE_URL}/integrations/${uuid}`,
      params,
    );

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// `meta` carries the Shared Settings cascade summary — which linked integrations
// the location was also added to — so it has to survive back to the caller.
const addIntegrationToLocation = async (attributes) => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_BASE_URL}/configured-integrations`,
      {
        data: {
          type: 'configured_integrations',
          attributes,
        },
      },
    );

    return { data: data.data, meta: data.meta };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const addIntegrationToMultipleLocations = async (
  integrationUuid,
  encodedCsvFile,
) => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_BASE_URL}/configured-integrations/bulk-create`,
      {
        data: {
          type: 'configured_integrations',
          attributes: {
            integration_uuid: integrationUuid,
            encoded_csv_file: encodedCsvFile,
          },
        },
      },
    );

    return { data: data.data, links: data.links };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const createIntegration = async (integrationAttributes) => {
  try {
    const { data } = await api.post(
      `${process.env.REACT_APP_BASE_URL}/integrations`,
      {
        data: {
          type: 'integrations',
          attributes: integrationAttributes,
        },
      },
    );

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchTemplateFile = async (uuid) => {
  try {
    const data = await api.get(`/integrations/${uuid}/bulk-template`, {
      responseType: 'blob',
    });

    return data;
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const clearCache = async (uuid) => {
  try {
    const data = await api.post(`/integrations/${uuid}/clean-cache`, {
      data: {},
    });

    return data;
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const exportLocations = async (uuid, params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const data = await api.get(
      `/integrations/${uuid}/export-locations${
        queryString ? `?${queryString}` : ''
      }`,
    );

    return data;
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchIntegrationBySlug = async (slug) => {
  try {
    const { data } = await api.get(
      `/integrations?filter[slug_eq]=${slug}&include=integration_step_functions`,
    );
    return { data: data.data, included: data.included };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const exportIntegrations = async (uuids) => {
  try {
    const { data } = await api.post('/integrations/export', {
      data: { type: 'integration_exports', attributes: { uuids } },
    });

    return { data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const importIntegrations = async (bundle, { preview = false } = {}) => {
  try {
    const body = preview
      ? { ...bundle, meta: { ...bundle.meta, mode: 'preview' } }
      : bundle;
    const { data } = await api.post('/integrations/import', body);

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchImportStatus = async (id) => {
  try {
    const { data } = await api.get(`/integration-imports/${id}`);

    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

export default {
  fetchIntegrations,
  fetchArchivedIntegrations,
  fetchIntegrationChangeHistory,
  addIntegrationToLocation,
  addIntegrationToMultipleLocations,
  fetchIntegrationBasicInfo,
  fetchIntegrationByUuid,
  toggleConfiguredIntegration,
  toggleIntegration,
  updateIntegrationStaticAttributes,
  updateIntegration,
  createIntegration,
  archiveIntegration,
  restoreIntegration,
  fetchTemplateFile,
  clearCache,
  exportLocations,
  fetchIntegrationBySlug,
  exportIntegrations,
  importIntegrations,
  fetchImportStatus,
};
